import { useMemo } from 'react'
import { OrderProgressEntity } from 'orders/domain/entities/order.entity'
import { OPERATION_MODE_CODES } from 'core/domain/operationModeConstants'

type DetailView = 'rule-table' | 'current'
type DetailStatus = 'loading' | 'error' | 'ready'

interface Params {
    progress: OrderProgressEntity | null
    loading: boolean
    error: boolean
}

interface Result {
    status: DetailStatus
    view: DetailView | null
    columns: string[]
}

const SORTER_COLUMNS = ['Chute', 'Regla', 'Paquetes']
const CLASSIFICATION_COLUMNS = ['Regla', 'Paquetes']

/**
 * Chooses the order-detail view from containerization and the table columns
 * from the operation mode (SM-1118).
 *
 * The view is decided ONLY by withoutContainerization, never by the mode:
 * - true  -> reusable rule table (columns by mode).
 * - false -> current containerized view.
 *
 * When the detail cannot be loaded (or the flag is undeterminable) the status
 * is 'error' and no view is inferred from the mode.
 */
export function useOrderDetailView({ progress, loading, error }: Params): Result {
    return useMemo<Result>(() => {
        if (loading) return { status: 'loading', view: null, columns: [] }
        if (error || !progress) return { status: 'error', view: null, columns: [] }

        const isSorter = progress.classificationType === OPERATION_MODE_CODES.SORTER
        const columns = isSorter ? SORTER_COLUMNS : CLASSIFICATION_COLUMNS

        return {
            status: 'ready',
            view: progress.withoutContainerization ? 'rule-table' : 'current',
            columns,
        }
    }, [progress, loading, error])
}
